import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  contact as fallbackContact,
  highlights as fallbackHighlights,
  services as fallbackServices,
  coverage as fallbackCoverage,
  headquarters as fallbackHq,
  clientPortalUrl,
} from "@/content/site";

export type BrandContent = {
  about: string;
  phoneDisplay: string;
  email: string;
  whatsapp: string;
  clientPortalUrl: string;
  /** Caminho no storage (ou URL) do logo. Vazio = logo padrão. */
  logoUrl: string;
  /** Imagem padrão do hero quando o banner não tem imagem própria. */
  heroImage: string;
};

export type HighlightItem = { value: string; label: string; icon: string };
export type ServiceItem = {
  icon: string;
  tag: string;
  title: string;
  text: string;
  ctaLabel?: string;
  ctaHref?: string;
};

export type ServicesContent = { title: string; items: ServiceItem[] };

export type CoverageContent = {
  title: string;
  text: string;
  ctaLabel: string;
  ctaTitle: string;
  hqName: string;
  hqAddress: string;
  hqDistrict: string;
  hqZip: string;
};

export type FooterLink = { label: string; to: string };
export type FooterContent = {
  supportNote: string;
  columns: { title: string; links: FooterLink[] }[];
};

export type PartnerItem = { name: string; image: string };
export type PartnersContent = {
  title: string;
  subtitle: string;
  items: PartnerItem[];
};

export type PhonePlanItem = {
  name: string;
  plan: string;
  price: string;
  note: string;
  badge?: string;
  ctaLabel?: string;
  features?: string[];
};
export type PhoneContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  /** Parágrafo de apresentação abaixo do topo da página. */
  text: string;
  items: PhonePlanItem[];
};

export type HostingPlanItem = {
  name: string;
  price: string;
  disk: string;
  transfer: string;
  domains: string;
  emails: string;
  sending: string;
  platform: string;
  /** Tabela "você poderá criar" */
  cpanel?: string;
  subdomains?: string;
  extraDomains?: string;
  pop3?: string;
  databases?: string;
  ftp?: string;
  badge?: string;
};
export type HostingContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  /** Parágrafo de apresentação abaixo do topo da página. */
  text: string;
  items: HostingPlanItem[];
  panelTitle: string;
  /** Um recurso por linha. */
  panelFeatures: string;
  emailTitle: string;
  /** Um recurso por linha. */
  emailFeatures: string;
  ctaLabel: string;
};

export type AppLogo = { name: string; image: string };

export type InternetContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  /** Parágrafo de apresentação abaixo do topo da página. */
  text: string;
  ctaLabel: string;
  /** Logos dos apps parceiros exibidos nos cards de plano. */
  apps: AppLogo[];
  bannerTitle: string;
  bannerText: string;
  bannerImage: string;
  bannerCtaLabel: string;
  bannerCtaHref: string;
};

export type SimplePlanItem = {
  name: string;
  price: string;
  note: string;
  badge?: string;
  ctaLabel?: string;
  features?: string[];
};

export type CamerasContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  text: string;
  items: SimplePlanItem[];
};

export type AppsContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  text: string;
  items: SimplePlanItem[];
  apps: AppLogo[];
};

export type DataCenterContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  text: string;
  items: SimplePlanItem[];
};

export type BlogPost = {
  title: string;
  date: string;
  excerpt: string;
  image: string;
  /** Endereço do post no site (ex.: minha-noticia). Gerado do título se vazio. */
  slug?: string;
  /** Texto completo do post, um parágrafo por linha. */
  body?: string;
};

export type BlogContent = {
  title: string;
  subtitle: string;
  items: BlogPost[];
};

export type JobItem = { role: string; location: string; url: string };
export type JobsContent = {
  title: string;
  subtitle: string;
  items: JobItem[];
  talentTitle: string;
  talentText: string;
  talentCtaLabel: string;
  talentCtaHref: string;
};

export type StoreItem = {
  name: string;
  address: string;
  district: string;
  zip: string;
  mapUrl: string;
};
export type StoresContent = {
  title: string;
  subtitle: string;
  items: StoreItem[];
};

export type AboutContent = {
  title: string;
  /** Parágrafos separados por linha em branco. */
  text: string;
  missionTitle: string;
  mission: string;
  visionTitle: string;
  vision: string;
  valuesTitle: string;
  /** Um valor por linha. */
  values: string;
};

export type PromoContent = {
  enabled: boolean;
  badge: string;
  title: string;
  text: string;
  image: string;
  ctaLabel: string;
  ctaHref: string;
};

export type IntegrationsContent = {
  /** ID/UUID do widget Huggy (Configurações → Widget). */
  huggyId: string;
  dataCenterUrl: string;
  webmailUrl: string;
  pixUrl: string;
};

export type SiteContent = {
  brand: BrandContent;
  highlights: HighlightItem[];
  services: ServicesContent;
  coverage: CoverageContent;
  partners: PartnersContent;
  internet: InternetContent;
  phone: PhoneContent;
  hosting: HostingContent;
  cameras: CamerasContent;
  apps: AppsContent;
  dataCenter: DataCenterContent;
  blog: BlogContent;
  jobs: JobsContent;
  stores: StoresContent;
  about: AboutContent;
  promo: PromoContent;
  integrations: IntegrationsContent;
  footer: FooterContent;
};

export const contentDefaults: SiteContent = {
  brand: {
    about:
      "Provedor de internet via fibra óptica e rádio, telefonia e hospedagem para residências e empresas.",
    phoneDisplay: fallbackContact.phoneDisplay,
    email: fallbackContact.email,
    whatsapp: fallbackContact.whatsapp,
    clientPortalUrl,
    logoUrl: "",
    heroImage: "",
  },
  highlights: fallbackHighlights.map((h) => ({ ...h })),
  services: { title: "Nossos serviços", items: fallbackServices.map((s) => ({ ...s })) },
  coverage: {
    title: fallbackCoverage.title,
    text: fallbackCoverage.text,
    ctaLabel: fallbackCoverage.ctaLabel,
    ctaTitle: fallbackCoverage.ctaTitle,
    hqName: fallbackHq.name,
    hqAddress: fallbackHq.address,
    hqDistrict: fallbackHq.district,
    hqZip: fallbackHq.zip,
  },
  partners: {
    title: "+1000 empresas confiam na Contato",
    subtitle:
      "Conectividade, telefonia e cloud para negócios de todos os tamanhos na nossa região.",
    items: [
      { name: "Supermercados Rede Sul", image: "" },
      { name: "Clínica Vida", image: "" },
      { name: "Construtora Horizonte", image: "" },
      { name: "Auto Peças Litoral", image: "" },
      { name: "Colégio Progresso", image: "" },
      { name: "Hotel Mar Azul", image: "" },
      { name: "Transportes Araranguá", image: "" },
      { name: "Padaria Pão Nosso", image: "" },
    ],
  },

  phone: {
    eyebrow: "Contato Telefonia",
    title: "Planos de telefonia Fale Mais",
    subtitle:
      "Fale à vontade com a telefonia fixa da Contato, na mesma fibra da sua internet e em um único boleto.",
    text: "Os planos Fale Mais oferecem ligações ilimitadas, portabilidade gratuita do seu número atual e recursos como identificador de chamadas, caixa postal e siga-me. Tudo instalado pela nossa equipe e com o suporte local de sempre, sem fidelidade escondida.",
    items: [
      {
        name: "Fale Mais",
        plan: "Local",
        price: "R$ 29,90 / mês",
        note: "Combo com internet",
        ctaLabel: "Contratar",
        features: [
          "Ligações ilimitadas para fixos locais",
          "Portabilidade gratuita do seu número",
          "Assinatura inclusa, sem taxa de habilitação",
          "Identificador de chamadas e caixa postal",
          "Tarifas reduzidas para celular e DDD",
          "Conta única com a sua internet",
        ],
      },
      {
        name: "Fale Mais",
        plan: "Plus",
        price: "R$ 49,90 / mês",
        note: "Combo com internet",
        badge: "Mais vendido",
        ctaLabel: "Contratar",
        features: [
          "Ligações ilimitadas para fixos locais e DDD (todo o Brasil)",
          "Portabilidade gratuita do seu número",
          "Assinatura inclusa, sem taxa de habilitação",
          "Identificador de chamadas, caixa postal e siga-me",
          "Tarifas reduzidas para celular",
          "Conta única com a sua internet",
        ],
      },
    ],
  },

  hosting: {
    eyebrow: "Contato Hospedagem",
    title: "Planos de hospedagem de sites",
    subtitle:
      "Profissionalismo que o seu negócio merece: servidores Linux, cPanel, e-mails profissionais e suporte de gente de verdade.",
    text: "Hospede o site da sua empresa em servidores estáveis, com painel cPanel, contas de e-mail profissionais no seu domínio, banco de dados e backups. Planos para todos os tamanhos de projeto e uma equipe local pronta para ajudar na migração e no dia a dia.",
    items: [
      {
        name: "Compact",
        price: "9,90",
        disk: "500 MB",
        transfer: "5 GB",
        domains: "1",
        emails: "10",
        sending: "120/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "não",
        extraDomains: "não",
        pop3: "5",
        databases: "1",
        ftp: "1",
      },
      {
        name: "Basic",
        price: "14,90",
        disk: "1 GB",
        transfer: "10 GB",
        domains: "2",
        emails: "20",
        sending: "120/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "2",
        extraDomains: "2",
        pop3: "20",
        databases: "2",
        ftp: "2",
      },
      {
        name: "Advanced",
        price: "19,90",
        disk: "1.5 GB",
        transfer: "25 GB",
        domains: "5",
        emails: "30",
        sending: "120/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "5",
        extraDomains: "5",
        pop3: "30",
        databases: "5",
        ftp: "5",
      },
      {
        name: "Professional",
        price: "29,90",
        disk: "2 GB",
        transfer: "35 GB",
        domains: "5",
        emails: "40",
        sending: "150/h",
        platform: "Linux",
        badge: "Mais vendido",
        cpanel: "1",
        subdomains: "5",
        extraDomains: "5",
        pop3: "30",
        databases: "5",
        ftp: "5",
      },
      {
        name: "Company",
        price: "39,90",
        disk: "2.5 GB",
        transfer: "100 GB",
        domains: "10",
        emails: "50",
        sending: "175/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "10",
        extraDomains: "10",
        pop3: "50",
        databases: "10",
        ftp: "10",
      },
      {
        name: "Enterprise",
        price: "49,90",
        disk: "5 GB",
        transfer: "200 GB",
        domains: "10",
        emails: "100",
        sending: "200/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "10",
        extraDomains: "10",
        pop3: "100",
        databases: "10",
        ftp: "10",
      },
      {
        name: "Corporate",
        price: "119,90",
        disk: "15 GB",
        transfer: "400 GB",
        domains: "15",
        emails: "Ilimitado",
        sending: "220/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "15",
        extraDomains: "15",
        pop3: "ilimitado",
        databases: "15",
        ftp: "15",
      },
    ],
    panelTitle: "Nosso painel de controle possui",
    panelFeatures: [
      "PHP MyAdmin",
      "Bloqueio de IPs",
      "Página de erros customizáveis",
      "Redirecionador de URL",
      "Gerenciador de arquivos via web",
      "Proteção de diretórios com senha",
      "Blogs",
      "PHP Nuke",
      "Fóruns",
      "Contadores",
      "Livro de visitas",
    ].join("\n"),
    emailTitle: "Componentes de e-mail",
    emailFeatures: [
      "Webmail",
      "Alias de e-mail",
      "Responder automático",
      "Lista de e-mail",
      "E-mail pega tudo",
      "Antispam (Spam Assassin)",
      "Redirecionador",
      "IMAP / POP3",
      "SMTP autenticado",
    ].join("\n"),
    ctaLabel: "Contratar",
  },

  internet: {
    eyebrow: "Contato Internet",
    title: "Planos de internet fibra óptica",
    subtitle:
      "Fibra 100% óptica com Wi-Fi incluso, instalação grátis e suporte de gente de verdade. Escolha a velocidade ideal para a sua casa e ainda leve apps de streaming e música inclusos no plano.",
    text: "Somos um provedor regional com rede 100% fibra óptica, atendimento próximo e técnicos da nossa própria equipe. Escolha a velocidade ideal, assine em poucos minutos e conte com Wi-Fi incluso, instalação grátis e apps de streaming no mesmo plano.",
    ctaLabel: "Assine agora",
    apps: [
      { name: "Deezer", image: "/apps/deezer.svg" },
      { name: "Max", image: "/apps/max.svg" },
      { name: "Looke", image: "/apps/looke.svg" },
      { name: "Paramount+", image: "/apps/paramount-plus.svg" },
    ],
    bannerTitle: "Indique a Contato e ganhe desconto",
    bannerText:
      "Indique um amigo, ele assina a fibra da Contato e vocês dois ganham desconto na mensalidade.",
    bannerImage: "",
    bannerCtaLabel: "Quero indicar",
    bannerCtaHref: "whatsapp:Olá! Quero indicar um amigo para a Contato.",
  },

  cameras: {
    eyebrow: "Contato Câmeras",
    title: "Monitoramento por câmeras na sua casa ou empresa",
    subtitle:
      "Instalação, configuração e suporte da Contato. Assista tudo pelo celular, de onde estiver, com imagens gravadas na nuvem.",
    text: "Nossos planos de monitoramento incluem câmeras de alta definição, aplicativo para celular, gravação em nuvem e visita técnica de instalação feita pela nossa equipe. Ideal para residências, comércios e empresas que precisam de segurança sem complicação.",
    items: [
      {
        name: "Câmera Essencial",
        price: "R$ 49,90 / mês",
        note: "2 câmeras",
        ctaLabel: "Contratar",
        features: [
          "2 câmeras Full HD",
          "Gravação em nuvem por 7 dias",
          "App para celular",
          "Instalação inclusa",
        ],
      },
      {
        name: "Câmera Plus",
        price: "R$ 89,90 / mês",
        note: "4 câmeras",
        badge: "Mais vendido",
        ctaLabel: "Contratar",
        features: [
          "4 câmeras Full HD",
          "Gravação em nuvem por 15 dias",
          "Visão noturna e detecção de movimento",
          "App para celular e suporte 7 dias",
        ],
      },
      {
        name: "Câmera Empresa",
        price: "Sob consulta",
        note: "Projeto sob medida",
        ctaLabel: "Falar com consultor",
        features: [
          "Projeto com quantidade de câmeras sob medida",
          "Gravação em nuvem estendida",
          "Acessos por usuário e relatórios",
          "Manutenção preventiva",
        ],
      },
    ],
  },

  apps: {
    eyebrow: "Contato Aplicativos",
    title: "Entretenimento incluso no seu plano",
    subtitle:
      "Streaming, música e TV ao vivo no mesmo boleto da sua internet, sem cartão de crédito e sem burocracia.",
    text: "Assine os aplicativos que você mais usa direto com a Contato: tudo cai na mesma fatura da sua internet e a ativação é feita na hora pelo nosso atendimento. Assista no celular, na TV ou no computador, quando e onde quiser.",
    items: [
      {
        name: "Combo Essencial",
        price: "R$ 19,90 / mês",
        note: "1 aplicativo",
        ctaLabel: "Contratar",
        features: [
          "Escolha 1 app do catálogo",
          "Cobrança na fatura da internet",
          "Ativação imediata",
        ],
      },
      {
        name: "Combo Plus",
        price: "R$ 34,90 / mês",
        note: "2 aplicativos",
        badge: "Mais vendido",
        ctaLabel: "Contratar",
        features: [
          "Escolha 2 apps do catálogo",
          "Cobrança na fatura da internet",
          "Troque de app quando quiser",
        ],
      },
      {
        name: "Combo Família",
        price: "R$ 59,90 / mês",
        note: "4 aplicativos",
        ctaLabel: "Contratar",
        features: [
          "Escolha 4 apps do catálogo",
          "Perfis para toda a família",
          "Cobrança na fatura da internet",
        ],
      },
    ],
    apps: [
      { name: "Deezer", image: "/apps/deezer.svg" },
      { name: "Max", image: "/apps/max.svg" },
      { name: "Looke", image: "/apps/looke.svg" },
      { name: "Paramount+", image: "/apps/paramount-plus.svg" },
      { name: "Contato TV", image: "/apps/contato-tv.svg" },
      { name: "Watch Brasil", image: "/apps/watch-brasil.svg" },
    ],
  },

  dataCenter: {
    eyebrow: "Contato Data Center",
    title: "Infraestrutura de ponta para o seu negócio",
    subtitle:
      "Servidores dedicados, colocation e cloud privada em ambiente seguro, com a conectividade de alta performance da fibra Contato.",
    text: "Hospede seus sistemas, dados e aplicações na infraestrutura da Contato. Oferecemos rack, banda garantida, redundância elétrica e climatização controlada, com conectividade direta à nossa rede de fibra óptica e suporte técnico local.",
    items: [
      {
        name: "Cloud Server",
        price: "R$ 99,90 / mês",
        note: "Máquinas virtuais",
        ctaLabel: "Contratar",
        features: [
          "Máquina virtual em ambiente redundante",
          "Backup diário automatizado",
          "Escalabilidade sob demanda",
          "Painel de gestão incluso",
        ],
      },
      {
        name: "Colocation",
        price: "R$ 249,90 / mês",
        note: "Rack por U",
        badge: "Mais vendido",
        ctaLabel: "Contratar",
        features: [
          "Espaço de rack em datacenter Tier 3",
          "Banda garantida e IP fixo",
          "Energia e climatização redundantes",
          "Acesso remoto e suporte 24/7",
        ],
      },
      {
        name: "Servidor Dedicado",
        price: "Sob consulta",
        note: "Bare metal",
        ctaLabel: "Falar com consultor",
        features: [
          "Hardware dedicado sob medida",
          "Banda ilimitada* ou garantida",
          "IP fixo e firewall opcional",
          "Instalação e monitoramento pela Contato",
        ],
      },
    ],
  },

  blog: {
    title: "Blog Contato",
    subtitle: "Dicas de tecnologia, entretenimento e novidades da sua região.",
    items: [
      {
        title:
          "O Criciúma conquistou mais uma vitória importante na Série B do Campeonato Brasileiro",
        date: "07/07/2026",
        excerpt:
          "O Tigre venceu o Sport por 1 a 0 no Heriberto Hülse, com gol de Waguininho, e somou três pontos fundamentais na competição.",
        body: "O Tigre venceu o Sport por 1 a 0 no Heriberto Hülse, em mais uma noite de casa cheia no Sul catarinense.\nWaguininho marcou no segundo tempo e garantiu três pontos fundamentais na briga pelo acesso.\nQuem é cliente Contato assistiu tudo sem travar: fibra óptica de verdade, com upload alto e baixa latência para transmissões ao vivo.",
        image: "/blog/futebol.jpg",
      },
      {
        title: "Os destaques do Disney+ em junho: confira as estreias que movimentaram o streaming",
        date: "30/06/2026",
        excerpt:
          "Junho foi recheado de novidades para os assinantes do Disney+, com estreias de aventura, animação e séries premiadas.",
        body: "Junho trouxe uma leva forte de estreias no Disney+, entre aventuras, animações e séries premiadas.\nA dica é organizar a maratona por categoria e aproveitar os fins de semana.\nCom os planos de fibra da Contato você assiste em 4K em vários aparelhos ao mesmo tempo, sem fila e sem travar.",
        image: "/blog/streaming.jpg",
      },
      {
        title: "Criciúma vence o São Bernardo e segue firme na Série B",
        date: "29/06/2026",
        excerpt:
          "Gol de Otero, de pênalti, garantiu mais três pontos importantes para o Tigre no Estádio Heriberto Hülse.",
        body: "Gol de Otero, de pênalti, garantiu mais três pontos importantes para o Tigre no Estádio Heriberto Hülse.\nA equipe segue firme na parte de cima da tabela da Série B.\nAcompanhe os próximos jogos com a conexão estável da Contato, do celular à TV.",
        image: "/blog/futebol.jpg",
      },
      {
        title: "Lançamentos da HBO Max em maio de 2026: veja o que chegou ao catálogo",
        date: "27/05/2026",
        excerpt:
          "Confira os filmes e séries que entraram no catálogo da HBO Max e prepare a sua maratona.",
        body: "O catálogo da HBO Max recebeu novos filmes e séries em maio, com destaque para produções originais e clássicos que voltaram ao ar.\nSeparamos os títulos que valem a pena entrar na sua lista.\nTodos os planos de internet da Contato incluem Wi-Fi e velocidade de sobra para streaming em alta definição.",
        image: "/blog/streaming.jpg",
      },
      {
        title: "Jogos para curtir em família: diversão garantida para todas as idades",
        date: "12/04/2026",
        excerpt:
          "Selecionamos jogos online e offline para aproveitar a internet de verdade com quem você ama.",
        body: "Selecionamos jogos online e offline para aproveitar com quem você ama, das crianças aos avós.\nDos clássicos de tabuleiro digitais aos títulos cooperativos online, dá para reunir todo mundo na sala.\nCom o upload simétrico da fibra Contato, jogar online fica sem lag mesmo com a casa toda conectada.",
        image: "/blog/jogos-familia.jpg",
      },
      {
        title: "Lançamentos do Disney+ e HBO Max em março de 2026: prepare sua maratona",
        date: "10/03/2026",
        excerpt:
          "As estreias do mês nas duas plataformas para você assistir com a fibra da Contato.",
        body: "Março movimentou as duas maiores plataformas de streaming com estreias muito aguardadas.\nMontamos um resumo para você não perder nada e já programar a maratona.\nA fibra da Contato entrega estabilidade para assistir em qualquer tela, a qualquer hora.",
        image: "/blog/streaming.jpg",
      },
    ],
  },

  jobs: {
    title: "Trabalhe conosco",
    subtitle:
      "Venha fazer parte de um time catarinense que conecta mais de 30 municípios do Sul de Santa Catarina.",
    items: [
      { role: "Auxiliar de Faxina", location: "Araranguá", url: "" },
      { role: "Monitor de Parque — Contato Fazenda Parque", location: "Araranguá", url: "" },
      { role: "Técnico de Instalação", location: "Tubarão", url: "" },
      { role: "Assistente de Suporte", location: "Araranguá", url: "" },
      { role: "Cabista", location: "Araranguá", url: "" },
      { role: "Assistente de Suporte", location: "Sombrio - SC", url: "" },
      { role: "Técnico de Instalação", location: "Araranguá / Criciúma", url: "" },
    ],
    talentTitle: "Banco de talentos",
    talentText:
      "Caso você não se identifique com as vagas acima, deixe o seu currículo em nosso banco de talentos. Assim que tivermos uma vaga no seu perfil, entraremos em contato.",
    talentCtaLabel: "Cadastrar currículo",
    talentCtaHref: "whatsapp:Olá! Quero cadastrar meu currículo no banco de talentos da Contato.",
  },

  stores: {
    title: "Nossas lojas",
    subtitle: "Atendimento presencial em cinco cidades do Sul de Santa Catarina.",
    items: [
      {
        name: "Matriz Araranguá",
        address: "R. Pedro João Pereira, 900",
        district: "Mato Alto, Araranguá - SC",
        zip: "88904-150",
        mapUrl: "",
      },
      {
        name: "Filial Tubarão",
        address: "R. Sen. Richard, 54",
        district: "Centro, Tubarão - SC",
        zip: "88701-220",
        mapUrl: "",
      },
      {
        name: "Filial Criciúma",
        address: "R. Maria Nazario B. da Silva, 51",
        district: "Vila Nova Esperança, Criciúma - SC",
        zip: "88806-035",
        mapUrl: "",
      },
      {
        name: "Filial Sombrio",
        address: "Av. Papa João XXIII, 290",
        district: "Parque das Avenidas, Sombrio - SC",
        zip: "88960-000",
        mapUrl: "",
      },
      {
        name: "Filial Imbituba",
        address: "R. Alcino da Fonseca, 51",
        district: "Paes Leme, Imbituba - SC",
        zip: "88780-000",
        mapUrl: "",
      },
    ],
  },

  about: {
    title: "Sobre nós",
    text: [
      "A Contato Internet é uma empresa genuinamente catarinense e atua no mercado há 29 anos, conta com uma infraestrutura física bem localizada e que atende as mais modernas necessidades tecnológicas do mercado.",
      "Sua sede localiza-se em Araranguá e conta ainda com filial em Tubarão, Criciúma, Sombrio e Imbituba, atendendo por volta de 30 municípios no Sul do Estado.",
      "A Contato Internet busca sempre oferecer as melhores soluções em serviços de telecomunicações, internet e hospedagem de sites, está em contínuo crescimento e evolução, expandindo sua rede constantemente para melhor atender seus clientes, sejam eles pessoas físicas ou corporativas.",
      "Busca continuamente ampliar a sua gama de produtos e serviços, considerando que a qualidade, o melhor atendimento e a ética são os norteadores da empresa e dos que dela fazem parte.",
    ].join("\n\n"),
    missionTitle: "Missão",
    mission:
      "Promover a conectividade das pessoas e organizações, por meio dos melhores serviços de telecomunicações e internet do Sul de Santa Catarina, tendo a qualidade e a busca constante de tecnologia como diferenciais, de forma a facilitar a vida das pessoas e aproximá-las.",
    visionTitle: "Visão",
    vision:
      "Ser reconhecida como a empresa de melhor desempenho no setor de telecomunicações e internet de Santa Catarina.",
    valuesTitle: "Valores",
    values: [
      "Inovação tecnológica",
      "Ética",
      "Agilidade",
      "Qualidade",
      "Flexibilidade",
      "Valorização humana",
      "Treinamento constante / Capacitação",
    ].join("\n"),
  },

  promo: {
    enabled: true,
    badge: "Promoção",
    title: "Fibra de 800 Mega por R$ 169,80",
    text: "Instalação grátis, Wi-Fi incluso e +200 canais de TV. Consulte a disponibilidade na sua rua.",
    image: "",
    ctaLabel: "Quero essa oferta",
    ctaHref: "whatsapp:Olá! Vi a promoção no site e quero contratar.",
  },

  integrations: {
    huggyId: "",
    dataCenterUrl: "https://contatocloud.com.br/",
    webmailUrl: "http://mail.contato.net/email/",
    pixUrl: "http://pix.contato.net/",
  },

  footer: {
    supportNote: "Suporte 7 dias por semana",
    columns: [
      {
        title: "Contato",
        links: [
          { label: "Sobre nós", to: "/sobre" },
          { label: "Blog Contato", to: "/blog" },
          { label: "Trabalhe conosco", to: "/trabalhe-conosco" },
          { label: "Lojas", to: "/lojas" },
        ],
      },
      {
        title: "Clientes",
        links: [
          { label: "Área do cliente", to: "/area-do-cliente" },
          { label: "Segunda via do boleto", to: clientPortalUrl },
          { label: "Pague via PIX", to: "http://pix.contato.net/" },
          { label: "Webmail", to: "http://mail.contato.net/email/" },
        ],
      },
      {
        title: "Serviços",
        links: [
          { label: "Internet", to: "/planos" },
          { label: "Telefonia", to: "/telefonia" },
          { label: "Data Center", to: "https://contatocloud.com.br/" },
          { label: "Câmeras", to: "/cameras" },
          { label: "Aplicativos", to: "/aplicativos" },
          { label: "Hospedagem", to: "/hospedagem" },
        ],
      },
    ],
  },
};

export type SiteContentKey = keyof SiteContent;

export const siteContentQuery = {
  queryKey: ["site_content"],
  queryFn: async (): Promise<Partial<Record<SiteContentKey, unknown>>> => {
    const { data, error } = await supabase.from("site_content").select("key, value");
    if (error || !data) return {};
    const record: Record<string, unknown> = {};
    for (const row of data) record[row.key] = row.value;
    return record as Partial<Record<SiteContentKey, unknown>>;
  },
  staleTime: 30_000,
};

function merge<K extends SiteContentKey>(key: K, raw: unknown): SiteContent[K] {
  const fallback = contentDefaults[key];
  if (raw == null) return fallback;
  if (Array.isArray(fallback)) {
    return (Array.isArray(raw) && raw.length ? raw : fallback) as SiteContent[K];
  }
  if (typeof raw === "object") {
    return { ...(fallback as object), ...(raw as object) } as SiteContent[K];
  }
  return fallback;
}

/** Conteúdo editável de uma seção, com fallback para os valores padrão. */
export function useSiteSection<K extends SiteContentKey>(key: K): SiteContent[K] {
  const { data } = useQuery(siteContentQuery);
  return merge(key, data?.[key]);
}

export function useBrand() {
  return useSiteSection("brand");
}

export function waLink(phone: string, message: string) {
  return `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
}
